import { Avatar, Badge, Button, Card, CardHeader, Drawer, EmptyState, Icon, Popover, PopoverItem, Skeleton, formatDate, formatTime, useToast } from "@helmr/ui";
import { localDateOf } from "@helmr/contracts";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isApiError } from "../../api/client";
import { deleteLead, doneLeadTask, moveLead, patchLead, type Lead } from "../../api/leads";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useServices } from "../../app/services";
import { isUnmasked, telHref, whatsappHref } from "../../lib/phone";
import { FailureBody } from "../home/states";
import { LinkedTaskForm } from "../followups/LinkedTaskForm";
import { BookTrialDrawer } from "./BookTrial";
import { ConvertDrawer } from "./ConvertDrawer";
import { useInvalidateLeadData, useLead, useLeadTasks, useScope, useStages, useStaffNames, type Scope } from "./hooks";
import { leads as t } from "./strings";
import { Owner, SourceChip, StageBadge, leadName } from "./ui";
import s from "./leads.module.css";

function ProfileSkeleton() {
  return (
    <div className={s.page} aria-busy="true" data-testid="profile-skeleton">
      <div className={s.profileHead}><Skeleton width={44} height={44} /><div style={{ display: "grid", gap: 8, flex: 1 }}><Skeleton width="40%" height={24} /><Skeleton width="60%" height={14} /></div></div>
      <div className={s.grid}><Card><div className={s.skRows}><Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} /></div></Card><Card><div className={s.skRows}><Skeleton height={14} /><Skeleton height={14} /></div></Card></div>
    </div>
  );
}

const whenOf = (iso: string, sc: Scope) => `${formatDate(localDateOf(Date.parse(iso), sc.ctx.timeZone), sc.ctx.locale, "weekday")} ${formatTime(Date.parse(iso), sc.ctx.timeZone)}`;

export function LeadProfile() {
  const { id = "" } = useParams();
  const sc = useScope();
  const { client } = useServices();
  const nav = useNavigate();
  const toast = useToast();
  const q = useLead(sc, id);
  const stages = useStages(sc);
  const staff = useStaffNames();
  const refresh = useInvalidateLeadData();
  const [drawer, setDrawer] = useState<"trial" | "convert" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);

  if (sc.ctx.status !== "loading" && !areaAllows("leads", sc.ctx.has)) return <NoAccess />;
  if (q.isPending) return <ProfileSkeleton />;
  if (q.error) {
    if (isApiError(q.error) && q.error.status === 404) {
      return <Card><EmptyState icon="inbox" title={t.profile.notFound} description={t.profile.notFoundBody} action={<Button as={Link} to="/leads">{t.profile.back}</Button>} /></Card>;
    }
    return <Card><FailureBody error={q.error} title={t.profile.error} what="This lead" onRetry={() => void q.refetch()} /></Card>;
  }

  const lead = q.data;
  const name = leadName(lead);
  const callable = isUnmasked(lead.phone);
  const canEdit = sc.ctx.has("leads.edit");
  // Converting is POST /leads/:id/convert: leads.edit plus leads.convert where a member is created (D57 compound).
  const mayConvert = canEdit && sc.ctx.has("leads.convert");
  const canConvert = mayConvert && !lead.memberId;
  const stage = stages.data?.find((x) => x.id === lead.stageId);

  async function move(stageId: string) {
    const to = stages.data?.find((x) => x.id === stageId);
    if (!to) return;
    if (to.type === "won" && !lead.memberId) {
      if (mayConvert) setDrawer("convert"); else toast.show(t.convert.needsRole);
      return;
    }
    try { await moveLead(client, lead.id, stageId); await refresh(); toast.show(t.profile.moved); }
    catch (err) { toast.show(isApiError(err) ? err.message : t.profile.moveFailed); }
  }
  async function trialOutcome(kind: "done" | "no_show") {
    setBusy(true);
    try {
      await patchLead(client, lead.id, kind === "done" ? { trialDoneAt: new Date(sc.nowMs).toISOString() } : { trialNoShowAt: new Date(sc.nowMs).toISOString() });
      await refresh();
    } catch (err) { toast.show(isApiError(err) ? err.message : t.profile.trialFailed); } finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true);
    try { await deleteLead(client, lead.id); await refresh(); toast.show(t.del.done); nav("/leads", { replace: true }); }
    catch (err) { toast.show(isApiError(err) ? err.message : t.del.failed); setBusy(false); }
  }

  return (
    <div className={s.page}>
      <title>{name} · Helmr</title>
      <div>
        <Link to="/leads" className={s.back}>← {t.profile.back}</Link>
        <div className={s.profileHead}>
          <Avatar id={lead.id} name={name} size="md" />
          <div>
            <h1 tabIndex={-1} title={name}>{name}</h1>
            <div className={s.chips}>
              <span>{callable ? lead.phone : <span title={t.profile.masked}>{lead.phone}</span>}</span>
              <SourceChip source={lead.source} />
              <StageBadge stage={stage} />
              <Owner id={lead.ownerId} name={staff.nameOf(lead.ownerId)} />
              {lead.memberId ? <Badge tone="ok">{t.profile.member}</Badge> : null}
            </div>
          </div>
          <div className={s.actions} role="group" aria-label="Lead actions">
            {callable ? <Button as="a" href={telHref(lead.phone)} aria-label={`${t.profile.call} ${name}`}><Icon name="phone" />{t.profile.call}</Button> : null}
            {callable ? <Button as="a" href={whatsappHref(lead.phone)} target="_blank" rel="noopener noreferrer" aria-label={`${t.profile.whatsapp} ${name}`}><Icon name="chat" />{t.profile.whatsapp}</Button> : null}
            {canEdit ? <Button onClick={() => setDrawer("trial")}>{t.bookTrial}</Button> : null}
            {canConvert ? <Button variant="primary" onClick={() => setDrawer("convert")}>{t.profile.convert}</Button> : null}
            {lead.memberId ? <Button as={Link} to={`/members/${lead.memberId}`}>{t.profile.openMember}</Button> : null}
            {sc.ctx.has("leads.move") && stages.data ? (
              <Popover role="menu" label={t.profile.move} trigger={(tp) => <Button {...tp}>{t.profile.move}</Button>}>
                {({ close }) => stages.data!.filter((x) => x.id !== lead.stageId).map((x) => <PopoverItem key={x.id} onClick={() => { close(); void move(x.id); }}>{x.name}</PopoverItem>)}
              </Popover>
            ) : null}
            {sc.ctx.has("leads.delete") ? <Button onClick={() => setDrawer("delete")}>{t.profile.delete}</Button> : null}
          </div>
        </div>
      </div>

      <div className={s.grid}>
        <div className={s.stack}>
          <Card aria-labelledby="h-details">
            <CardHeader titleId="h-details" title={t.profile.details} />
            <dl className={s.kv}>
              <dt>{t.profile.phone}</dt><dd>{lead.phone}{callable ? null : <> <small className={s.muted}>{t.profile.maskedPhone}</small></>}</dd>
              <dt>{t.profile.email}</dt><dd>{lead.email ?? <span className={s.muted}>{t.profile.none}</span>}</dd>
              <dt>{t.profile.branch}</dt><dd>{sc.ctx.me?.branches.find((b) => b.id === lead.branchId)?.name ?? <span className={s.muted}>{t.profile.none}</span>}</dd>
              <dt>{t.profile.owner}</dt><dd><Owner id={lead.ownerId} name={staff.nameOf(lead.ownerId)} /></dd>
              <dt>{t.profile.source}</dt><dd><SourceChip source={lead.source} /></dd>
              <dt>{t.profile.added}</dt><dd>{whenOf(lead.createdAt, sc)}</dd>
              <dt>{t.profile.lastSeen}</dt><dd>{whenOf(lead.lastSeenAt, sc)}</dd>
            </dl>
          </Card>
          <TrialCard lead={lead} sc={sc} canEdit={canEdit} busy={busy} onBook={() => setDrawer("trial")} onOutcome={trialOutcome} />
        </div>
        <div className={s.stack}>
          <LeadTasks lead={lead} sc={sc} canEdit={canEdit} />
          <Card aria-labelledby="h-touches">
            <CardHeader titleId="h-touches" title={t.profile.touches} subtitle={t.profile.touchesSub} />
            {(lead.touches ?? []).length === 0 ? <EmptyState compact icon="clock" title={t.profile.touchesEmpty} /> : (
              <ol className={s.tl}>
                {[...(lead.touches ?? [])].reverse().map((x) => (
                  <li key={x.seq}><i aria-hidden="true" /><div><b>{t.sources[x.source.toLowerCase()] ?? x.source}</b>{x.campaign ? <> · {t.profile.campaign(x.campaign)}</> : null}<small>{whenOf(x.at, sc)}</small></div></li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      {drawer === "trial" ? <BookTrialDrawer sc={sc} lead={lead} open onClose={() => setDrawer(null)} /> : null}
      {drawer === "convert" && stages.data ? <ConvertDrawer sc={sc} lead={lead} stages={stages.data} open onClose={() => setDrawer(null)} onConverted={() => setDrawer(null)} /> : null}
      <Drawer open={drawer === "delete"} onOpenChange={(o) => { if (!o && !busy) setDrawer(null); }} title={t.del.title(name)} description={t.del.body}
        footer={<><Button onClick={() => setDrawer(null)} disabled={busy}>{t.del.cancel}</Button><Button variant="primary" onClick={() => void remove()} busy={busy} busyLabel={t.del.deleting}>{t.del.confirm}</Button></>}>
        <p className={s.drawerPad}>{t.del.body}</p>
      </Drawer>
    </div>
  );
}

function TrialCard({ lead, sc, canEdit, busy, onBook, onOutcome }: { lead: Lead; sc: Scope; canEdit: boolean; busy: boolean; onBook: () => void; onOutcome: (k: "done" | "no_show") => void }) {
  const open = lead.trialAt && !lead.trialDoneAt && !lead.trialNoShowAt;
  return (
    <Card aria-labelledby="h-trial">
      <CardHeader titleId="h-trial" title={t.profile.trial} />
      <div className={s.row}>
        <div>
          {!lead.trialAt ? <span className={s.muted}>{t.profile.trialNone}</span> : lead.trialDoneAt ? <b>{t.profile.trialDone(whenOf(lead.trialDoneAt, sc))}</b> : lead.trialNoShowAt ? <b>{t.profile.trialNoShow(whenOf(lead.trialNoShowAt, sc))}</b> : <b>{t.profile.trialBooked(whenOf(lead.trialAt, sc))}</b>}
        </div>
        {canEdit ? (
          <div className={s.actions}>
            {open ? <><Button size="sm" onClick={() => onOutcome("no_show")} disabled={busy}>{t.profile.trialMarkNoShow}</Button><Button size="sm" variant="primary" onClick={() => onOutcome("done")} disabled={busy}>{t.profile.trialMarkDone}</Button></> : null}
            {!lead.trialAt || lead.trialDoneAt || lead.trialNoShowAt ? <Button size="sm" onClick={onBook}>{t.profile.trialBook}</Button> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function LeadTasks({ lead, sc, canEdit }: { lead: Lead; sc: Scope; canEdit: boolean }) {
  const { client } = useServices();
  const toast = useToast();
  const q = useLeadTasks(sc);
  const refresh = useInvalidateLeadData();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const mine = (q.data ?? []).filter((x) => x.leadId === lead.id);

  async function done(key: string) {
    setBusyKey(key);
    try { await doneLeadTask(client, lead.id, key); await refresh(); } catch { toast.show(t.profile.taskFailed); } finally { setBusyKey(null); }
  }

  return (
    <Card aria-labelledby="h-ltasks">
      <CardHeader titleId="h-ltasks" title={t.profile.tasks} subtitle={t.profile.tasksSub} />
      {q.isPending ? <div className={s.skRows} aria-busy="true"><Skeleton height={14} /><Skeleton width="70%" height={14} /></div> : q.error ? (
        <FailureBody error={q.error} title={t.profile.error} what="Lead tasks" onRetry={() => void q.refetch()} />
      ) : mine.length === 0 ? <EmptyState compact icon="check" title={t.profile.tasksEmpty} /> : (
        <div className={s.list}>
          {mine.map((x) => (
            <div className={s.row} key={x.key}>
              <div><b>{t.profile.taskKinds[x.kind] ?? x.kind}</b><small className={x.overdueMs > 0 ? s.od : undefined}>{x.overdueMs > 0 ? `${t.profile.overdue} · ` : ""}{whenOf(x.dueAt, sc)}</small></div>
              {canEdit ? <Button size="sm" onClick={() => void done(x.key)} busy={busyKey === x.key} busyLabel="Saving…" aria-label={`${t.profile.taskDone}: ${t.profile.taskKinds[x.kind] ?? x.kind}`}>{t.profile.taskDone}</Button> : null}
            </div>
          ))}
        </div>
      )}
      {canEdit ? <LinkedTaskForm sc={sc} link={{ type: "lead", id: lead.id }} /> : null}
    </Card>
  );
}
